import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { fileUrl } from "@/lib/api";
import { TrendingUp, Heart, Flame } from "lucide-react";
import StarRating from "@/components/StarRating";
import VerifiedBadge from "@/components/VerifiedBadge";
import { useFavorites } from "@/context/FavoritesContext";

export const formatPrice = (p, freeLabel) => (p === 0 ? freeLabel : `$${Number(p).toFixed(Number(p) % 1 === 0 ? 0 : 2)}`);

export default function ProductCard({ product, index = 0 }) {
  const { t } = useI18n();
  const { isFav, toggle } = useFavorites();
  const navigate = useNavigate();
  const fav = isFav(product.id);

  const onHeart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const res = await toggle(product.id);
    if (res?.needAuth) navigate("/login");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.4) }}
    >
      <Link
        to={`/product/${product.id}`}
        data-testid={`product-card-${product.id}`}
        className="group block bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200/60 hover:border-[#0EA5E9]/40 transition-[transform,box-shadow,border-color] duration-300"
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-[#F1F5F9]">
          {product.preview_image_url ? (
            <img
              src={fileUrl(product.preview_image_url)}
              alt={product.title}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-sky-100 to-amber-50" />
          )}
          {product.category && (
            <span className="absolute top-3 left-3 glass text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full text-[#0F172A]">
              {product.category}
            </span>
          )}
          {product.best_seller && (
            <span className="absolute bottom-3 left-3 flex items-center gap-1 bg-[#F59E0B] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full shadow-lg shadow-amber-500/30" data-testid="best-seller-badge">
              <Flame className="h-3 w-3" /> {t("product.bestSeller")}
            </span>
          )}
          <button
            onClick={onHeart}
            data-testid={`fav-btn-${product.id}`}
            aria-label="favorite"
            className="absolute top-3 right-3 h-9 w-9 rounded-full glass flex items-center justify-center hover:scale-110 transition-transform"
          >
            <Heart className={`h-4 w-4 ${fav ? "fill-red-500 text-red-500" : "text-[#0F172A]"}`} />
          </button>
        </div>
        <div className="p-4">
          <h3 className="font-display font-bold text-[#0F172A] leading-snug line-clamp-2 min-h-[2.6rem]">
            {product.title}
          </h3>
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-2 min-w-0">
              {product.seller_avatar ? (
                <img src={fileUrl(product.seller_avatar)} alt="" className="h-6 w-6 rounded-full object-cover" />
              ) : (
                <div className="h-6 w-6 rounded-full bg-[#0F172A] text-white text-[10px] font-bold flex items-center justify-center">
                  {(product.seller_name || "F")[0]}
                </div>
              )}
              <span className="text-xs text-[#475569] truncate">{product.seller_name}</span>
              {product.seller_verified && <VerifiedBadge size={13} />}
            </div>
            <span className="font-display font-extrabold text-[#0F172A]">{formatPrice(product.price, t("common.free"))}</span>
          </div>
          <div className="flex items-center justify-between mt-2 min-h-[16px]">
            {product.rating_count > 0 ? (
              <StarRating value={product.rating_avg} count={product.rating_count} size={13} />
            ) : <span />}
            {product.sales_count > 0 && (
              <div className="flex items-center gap-1 text-[11px] text-[#94A3B8]">
                <TrendingUp className="h-3 w-3" /> {product.sales_count} {t("product.sales")}
              </div>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
